import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import classNames from 'classnames';
import React, { useState, useEffect, useId } from 'react';
import { Ratio } from 'react-bootstrap';
import { create } from '@lottiefiles/lottie-interactivity';
import { Image } from '../image/image.esm.js';

var PlayMode;
(function(PlayMode) {
    PlayMode["Scroll"] = "scroll";
    PlayMode["Auto"] = "auto";
    PlayMode["Manual"] = "manual";
})(PlayMode || (PlayMode = {}));
var ActionType;
(function(ActionType) {
    ActionType["Seek"] = "seek";
    ActionType["Stop"] = "stop";
    ActionType["Play"] = "play";
    ActionType["Hold"] = "hold";
    ActionType["PauseHold"] = "pauseHold";
    ActionType["PlayOnce"] = "playOnce";
    ActionType["Loop"] = "loop";
    ActionType["Click"] = "click";
    ActionType["Hover"] = "hover";
    ActionType["Toggle"] = "toggle";
})(ActionType || (ActionType = {}));
const TRANSPARENT_PLACEHOLDER_BG = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';
const LazyPlayer = /*#__PURE__*/ React.lazy(()=>import('@lottiefiles/react-lottie-player').then((module)=>({
            default: module.Player
        })));
const LottieAnimation = ({ animation: { src, placeholderImage = {
    src: TRANSPARENT_PLACEHOLDER_BG,
    width: 1600,
    height: 1200,
    priority: true,
    alt: 'Loading animation placeholder'
}, loop = true, keepLastFrame = true, playMode = PlayMode.Scroll, actionType = ActionType.Play, visibility = [
    0.3,
    0.8
], containerId }, onAnimationLoad, ...rest })=>{
    const [lottie, setLottie] = useState();
    useEffect(()=>{
        if (!lottie) {
            return;
        }
        const configureToPlayOnScroll = ()=>{
            create({
                mode: 'scroll',
                player: lottie,
                container: containerId,
                actions: [
                    {
                        visibility,
                        type: actionType
                    }
                ]
            });
        };
        if (playMode === PlayMode.Scroll) {
            configureToPlayOnScroll();
        }
        onAnimationLoad?.(lottie);
    }, [
        lottie
    ]);
    return jsxs("div", {
        ...rest,
        role: "presentation",
        className: "lottie-animation position-relative",
        children: [
            jsx(Ratio, {
                aspectRatio: "16x9",
                className: classNames('lottie-animation-placeholder-image opacity-0', {
                    'position-absolute': lottie
                }),
                role: "presentation",
                children: jsx(Image, {
                    priority: true,
                    src: placeholderImage?.src,
                    alt: placeholderImage?.alt,
                    width: placeholderImage?.width,
                    height: placeholderImage?.height,
                    "data-testid": "placeholder-image",
                    role: "presentation",
                    style: {
                        objectFit: 'contain'
                    }
                })
            }),
            jsx("div", {
                className: classNames('lottie-animation-player', {
                    'animate-fade-in': lottie
                }),
                children: jsx(React.Suspense, {
                    fallback: jsx(Fragment, {}),
                    children: jsx(LazyPlayer, {
                        id: useId(),
                        lottieRef: (instance)=>setLottie(instance),
                        autoplay: playMode === PlayMode.Auto,
                        loop: loop,
                        keepLastFrame: keepLastFrame,
                        src: src
                    })
                })
            })
        ]
    });
};

export { ActionType, LottieAnimation, PlayMode };
